import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Box, Checkbox, Tab, Tabs, Typography } from '@mui/material'

import Button from '../Button'
import Dropdown, { type DropdownOption } from '../Dropdown'
import Icon from '../Icon'
import IconButton from '../IconButton'
import TextField from '../TextField'

import './AnswerFilter.scss'

export interface FormField {
  id: string
  name: string
  formStepName: string
  valueType: string
  style: string
  choices?: string[]
}

export interface AnswerFilterEntry {
  fieldId: string | null
  mode: string
  selectedValues: string[]
  keyword: string
  applied: boolean
}

export interface AnswerFilterProps {
  fields: FormField[]
  value?: AnswerFilterEntry[]
  onChange?: (filters: AnswerFilterEntry[]) => void
  onApply?: (filters: AnswerFilterEntry[]) => void
  onReset?: () => void
}

export const createEmptyFilter = (): AnswerFilterEntry => ({
  fieldId: null,
  mode: 'is',
  selectedValues: [],
  keyword: '',
  applied: false,
})

const AnswerFilter: React.FC<AnswerFilterProps> = ({
  fields,
  value,
  onChange,
  onApply,
  onReset,
}) => {
  const [internalFilters, setInternalFilters] = useState<AnswerFilterEntry[]>(() => [
    createEmptyFilter(),
  ])
  const filters = value !== undefined ? value : internalFilters
  const filtersRef = useRef(filters)
  filtersRef.current = filters

  const setFilters = useCallback(
    (action: React.SetStateAction<AnswerFilterEntry[]>) => {
      const next = typeof action === 'function' ? action(filtersRef.current) : action
      onChange?.(next)
      setInternalFilters(next)
    },
    [onChange],
  )

  const hasChoices = (field: FormField): boolean =>
    field.style === 'DropdownInputStyle' ||
    field.style === 'RadioInputStyle' ||
    field.valueType === 'bool'

  const dropdownOptions: DropdownOption[] = useMemo(
    () =>
      fields.map((f) => ({
        value: f.id,
        label: f.name,
        group: f.formStepName,
      })),
    [fields],
  )

  const fieldMap = useMemo(() => {
    const map = new Map<string, FormField>()
    fields.forEach((f) => map.set(f.id, f))
    return map
  }, [fields])

  const updateFilter = useCallback(
    (index: number, patch: Partial<AnswerFilterEntry>) => {
      setFilters((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)))
    },
    [setFilters],
  )

  const removeFilter = useCallback(
    (index: number) => {
      setFilters((prev) => prev.filter((_, i) => i !== index))
    },
    [setFilters],
  )

  const addFilter = useCallback(() => {
    setFilters((prev) => [...prev, createEmptyFilter()])
  }, [setFilters])

  const resetAll = useCallback(() => {
    setFilters([])
    onReset?.()
  }, [onReset, setFilters])

  const handleFieldChange = useCallback(
    (index: number, value: string | string[]) => {
      const fieldId = Array.isArray(value) ? value[0] : value
      const field = fieldMap.get(fieldId)
      updateFilter(index, {
        fieldId,
        mode: field && !hasChoices(field) ? 'includes' : 'is',
        selectedValues: [],
        keyword: '',
      })
    },
    [fieldMap, updateFilter],
  )

  const handleToggleValue = useCallback(
    (index: number, value: string) => {
      setFilters((prev) =>
        prev.map((f, i) => {
          if (i !== index) return f
          const has = f.selectedValues.includes(value)
          return {
            ...f,
            selectedValues: has
              ? f.selectedValues.filter((v) => v !== value)
              : [...f.selectedValues, value],
          }
        }),
      )
    },
    [setFilters],
  )

  return (
    <Box className='answer-filter'>
      {filters.map((filter, index) => {
        const field = filter.fieldId ? fieldMap.get(filter.fieldId) : null
        const isSelectType = field ? hasChoices(field) : false
        const isTextType = field ? !hasChoices(field) : false

        const modeOptions = isTextType
          ? [
              { label: 'Includes', value: 'includes' },
              { label: 'Excludes', value: 'excludes' },
            ]
          : [
              { label: 'Is', value: 'is' },
              { label: 'Is not', value: 'is-not' },
            ]

        if (filter.applied && field) {
          const modeLabel =
            filter.mode === 'is'
              ? 'is'
              : filter.mode === 'is-not'
                ? 'is not'
                : filter.mode === 'includes'
                  ? 'includes'
                  : 'excludes'
          const valueSummary = isTextType ? filter.keyword : filter.selectedValues.join(' or ')

          return (
            <Box key={index} className='answer-filter-entry'>
              <Box className='answer-filter-card-header'>
                <Typography className='answer-filter-card-title'>Filter {index + 1}</Typography>
                <IconButton
                  size='small'
                  aria-label={`Remove filter ${index + 1}`}
                  onClick={() => removeFilter(index)}
                >
                  <Icon name='close' size='16px' />
                </IconButton>
              </Box>
              <Box className='answer-filter-card answer-filter-card--applied'>
                <Box className='answer-filter-applied-row'>
                  <Typography className='answer-filter-applied-summary'>
                    <span className='answer-filter-applied-field'>{field.name}</span>{' '}
                    <span className='answer-filter-applied-mode'>{modeLabel}</span>{' '}
                    <span className='answer-filter-applied-values'>{valueSummary}</span>
                  </Typography>
                  <IconButton
                    size='small'
                    aria-label={`Edit filter ${index + 1}`}
                    onClick={() => updateFilter(index, { applied: false })}
                  >
                    <Icon name='edit' size='16px' />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          )
        }

        return (
          <Box key={index} className='answer-filter-entry'>
            <Box className='answer-filter-card-header'>
              <Typography className='answer-filter-card-title'>Filter {index + 1}</Typography>
              <IconButton
                size='small'
                aria-label={`Remove filter ${index + 1}`}
                onClick={() => removeFilter(index)}
              >
                <Icon name='close' size='16px' />
              </IconButton>
            </Box>

            <Box className='answer-filter-card'>
              <Dropdown
                label='Input Field'
                placeholder='Select a field...'
                options={dropdownOptions}
                value={filter.fieldId ?? undefined}
                onChange={(val) => handleFieldChange(index, val)}
              />

              {field && (
                <>
                  <Tabs
                    value={filter.mode}
                    onChange={(_e, val) => updateFilter(index, { mode: val as string })}
                  >
                    {modeOptions.map((opt) => (
                      <Tab key={opt.value} label={opt.label} value={opt.value} />
                    ))}
                  </Tabs>

                  {isSelectType && field.choices && field.choices.length > 0 && (
                    <Box className='answer-filter-options-list'>
                      {field.choices.map((opt) => {
                        const checked = filter.selectedValues.includes(opt)
                        return (
                          <Box
                            key={opt}
                            className='answer-filter-option-row'
                            onClick={() => handleToggleValue(index, opt)}
                          >
                            <Checkbox
                              size='small'
                              checked={checked}
                              tabIndex={-1}
                              disableRipple
                              sx={{ p: 0 }}
                            />
                            <Typography className='answer-filter-option-label'>{opt}</Typography>
                          </Box>
                        )
                      })}
                    </Box>
                  )}

                  {isTextType && (
                    <TextField
                      placeholder={`Enter ${field.name.toLowerCase()}...`}
                      value={filter.keyword}
                      onChange={(e) => updateFilter(index, { keyword: e.target.value })}
                    />
                  )}

                  <Box className='answer-filter-apply-row'>
                    <Button
                      color='primary'
                      variant='contained'
                      size='small'
                      onClick={() => {
                        const updated = filters.map((f, i) =>
                          i === index ? { ...f, applied: true } : f,
                        )
                        setFilters(updated)
                        onApply?.(updated)
                      }}
                    >
                      Apply
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )
      })}

      <Box className='answer-filter-actions'>
        <Button color='primary' variant='outlined' size='small' onClick={addFilter}>
          Add Another Filter
        </Button>
        {filters.some((f) => f.fieldId) && (
          <button type='button' className='answer-filter-reset' onClick={resetAll}>
            Reset filter
          </button>
        )}
      </Box>
    </Box>
  )
}

export default AnswerFilter
